import {
	type JSONContent,
	type MarkdownLexerConfiguration,
	type MarkdownParseHelpers,
	type MarkdownRendererHelpers,
	type MarkdownToken,
	Node,
	type RenderContext,
} from "@tiptap/core";
import { attachIals, nextFence, prepare, unprepare } from "./ials";

// Box styles are written in Portuguese; any other class is written verbatim.
const NAMES = new Map([
	["roll", "rolagem"],
	["highlighted", "destaque"],
	["lesson", "licao"],
	["readme", "leia"],
	["rule", "regra"],
	["note-arkhi", "nota-arkhi"],
	["example-arkhi", "exemplo-arkhi"],
	["text-right", "direita"],
]);
const CLASSES = new Map([...NAMES].map(([cls, name]) => [name, cls]));
const mapNames = (names: string, table: Map<string, string>) =>
	names
		.split(" ")
		.map((name) => table.get(name) ?? name)
		.join(" ");

// These patterns are Markdown::Notation's; a short form is only written when Ruby reads it back.
const CONTAINER_OPEN = /^:::([a-z][\w-]*(?: [a-z][\w-]*)*)(?: \{([^}]*)\})?[ \t]*$/;
const TEST_OPEN = /^:::teste (\S.*?)[ \t]*$/;
const CLOSE = /^:::[ \t]*$/;
const CONTAINER_IAL = /^\{: class="([a-z][\w-]*(?: [a-z][\w-]*)*)"(?: (style="[^"}]*"))?\}$/;

// The short names of a box's classes, or null when Ruby would read them as another box or a dice test.
export function shortNames(classes: string): string | null {
	const names = classes.split(" ");
	if (names[0] === "teste" || names.some((name) => !NAMES.has(name) && CLASSES.has(name))) return null;
	return mapNames(classes, NAMES);
}

export const boxIal = (ial: string | undefined) => (ial ? CONTAINER_IAL.exec(ial) : null);

// Which lines Markdown::Notation.expand reads as notation: not fenced code, and not inside an HTML tag that
// began on an earlier line (code spans, `\\![`, stage tags and pictures hide a `<` from it).
const SKIP = /(`+)(?:(?!\1).)+\1|\\!\[|(<[a-zA-Z/!])|ESTAGIO\[[123]\]|!\[[^\]\n]*\]\([\w-]+(?:\/[\w.-]+)*\.\w+(?: ".*?")?\)/g;

// Where the tag open at `from` ends: the index past its `>`, or -1 and the quote it is left in.
function tagEnd(line: string, from: number, open: string): [number, string] {
	let quote = open;
	for (let i = from; i < line.length; i += 1) {
		if (quote) quote = line[i] === quote ? "" : quote;
		else if (line[i] === '"' || line[i] === "'") quote = line[i];
		else if (line[i] === ">") return [i + 1, ""];
	}
	return [-1, quote];
}

class Scanner {
	fence: string | null = null;
	tag: string | null = null;

	// Whether the line may carry notation; the box lines themselves leave the tag state alone.
	reads(line: string): boolean {
		const inside = this.fence;
		this.fence = nextFence(line, this.fence);
		if (inside || this.fence) return false;
		const reads = this.tag === null;
		if (reads && (CLOSE.test(line) || TEST_OPEN.test(line) || CONTAINER_OPEN.test(line))) return true;
		let from = 0;
		if (this.tag !== null) {
			const [end, quote] = tagEnd(line, 0, this.tag);
			this.tag = end < 0 ? quote : null;
			if (end < 0) return false;
			from = end;
		}
		SKIP.lastIndex = from;
		for (let m = SKIP.exec(line); m; m = SKIP.exec(line)) {
			if (!m[2]) continue;
			const [end, quote] = tagEnd(line, m.index + m[0].length, "");
			if (end < 0) {
				this.tag = quote;
				break;
			}
			SKIP.lastIndex = end;
		}
		return reads;
	}
}

// The `:::` lines of a body must open and close among themselves, outside fenced code, or Ruby would pair them with the box.
function balanced(body: string): boolean {
	let depth = 0;
	const scan = new Scanner();
	for (const line of body.split("\n")) {
		if (!scan.reads(line)) continue;
		if (CLOSE.test(line)) depth -= 1;
		else if (TEST_OPEN.test(line) || CONTAINER_OPEN.test(line)) depth += 1;
		if (depth < 0) return false;
	}
	return depth === 0;
}

// A dice test is a roll box of: bold title, a BLACKBOX[NdM] line, single-line paragraphs, then Sucesso and Falha
// sharing one threshold. expandTest is Markdown::Notation.test_box: the paragraphs the short lines stand for.
const DICE_LINE = /^(\S+)(.*)$/;
const SUCCESS_LINE = /^Sucesso (≤|>) (.+?): (.+)$/;
const FAILURE_LINE = /^Falha: (.+)$/;
const SUCCESS = /^Sucesso \((.+)\) quando rolar (≤|>) (.+)$/;
const FAILURE = /^Falha \((.+)\) quando rolar (≤|>) (.+)$/;

export function expandTest(title: string, body: string[]): string | null {
	const failure = FAILURE_LINE.exec(body.at(-1) ?? "");
	const success = SUCCESS_LINE.exec(body.at(-2) ?? "");
	const dice = DICE_LINE.exec(body[0] ?? "");
	const extra = body.slice(1, -2);
	if (body.length < 3 || !failure || !success || !dice || extra.some((line) => line.trim() === "")) return null;
	const other = success[1] === "≤" ? ">" : "≤";
	return [
		`**${title}**`,
		`BLACKBOX[${dice[1]}]${dice[2]}`,
		...extra,
		`Sucesso (${success[3]}) quando rolar ${success[1]} ${success[2]}`,
		`Falha (${failure[1]}) quando rolar ${other} ${success[2]}`,
	].join("\n\n");
}

function contractTest(body: string): string | null {
	const paragraphs = body.split("\n\n");
	const title = /^\*\*(\S|\S(?:(?!\*\*).)*\S)\*\*$/.exec(paragraphs[0]);
	const dice = /^BLACKBOX\[(\d+[dD]\d+)\](|\s.*)$/.exec(paragraphs[1] ?? "");
	const success = SUCCESS.exec(paragraphs.at(-2) ?? "");
	const failure = FAILURE.exec(paragraphs.at(-1) ?? "");
	if (!title || !dice || !success || !failure || paragraphs.length < 4) return null;
	if (success[2] === failure[2] || success[3] !== failure[3] || success[3].includes(": ")) return null;
	if (paragraphs.some((p) => p.trim() === "" || p.includes("\n"))) return null;
	const lines = [`${dice[1]}${dice[2]}`, ...paragraphs.slice(2, -2), `Sucesso ${success[2]} ${success[3]}: ${success[1]}`, `Falha: ${failure[1]}`];
	return expandTest(title[1], lines) === body ? `:::teste ${title[1]}\n${lines.join("\n")}\n:::` : null;
}

// Runs on the finished text: the atoms are back in place and every paragraph is a string.
export const contractTests = (markdown: string) => markdown.replace(/^:::rolagem\n([\s\S]*?)\n:::$/gm, (box, body) => contractTest(body) ?? box);

// `:::names` … `:::` is a blockquote carrying `{: class="…"}`.
export const Callout = Node.create({
	name: "callout",
	markdownTokenizer: {
		name: "callout",
		level: "block",
		start: (src: string) => src.search(/\n:::[a-z]/),
		tokenize: (src: string, _tokens: MarkdownToken[], lexer: MarkdownLexerConfiguration) => {
			const lines = src.split("\n");
			const test = TEST_OPEN.exec(lines[0]);
			const open = test ? null : CONTAINER_OPEN.exec(lines[0]);
			if (!test && !open) return undefined;
			let depth = 0;
			const scan = new Scanner();
			for (let i = 1; i < lines.length; i += 1) {
				if (!scan.reads(lines[i])) continue;
				if (TEST_OPEN.test(lines[i]) || CONTAINER_OPEN.test(lines[i])) depth += 1;
				else if (CLOSE.test(lines[i])) {
					if (depth > 0) {
						depth -= 1;
						continue;
					}
					const raw = lines.slice(0, i + 1).join("\n") + (i + 1 < lines.length ? "\n" : "");
					if (!test)
						return {
							type: "callout",
							raw,
							classes: mapNames(open?.[1] ?? "", CLASSES),
							attrs: open?.[2] ?? null,
							tokens: lexer.blockTokens(prepare(lines.slice(1, i).join("\n"))),
						};
					const inner = expandTest(test[1], lines.slice(1, i));
					return inner === null
						? { type: "callout", raw, verbatim: unprepare(raw.replace(/\n+$/, "")) }
						: { type: "callout", raw, classes: "roll", attrs: null, tokens: lexer.blockTokens(prepare(inner)) };
				}
			}
			return undefined;
		},
	},
	parseMarkdown: (token: MarkdownToken, helpers: MarkdownParseHelpers) =>
		token.verbatim
			? { type: "rawHtmlBlock", attrs: { raw: token.verbatim } }
			: {
					type: "blockquote",
					attrs: { ial: `{: class="${token.classes}"${token.attrs ? ` ${token.attrs}` : ""}}` },
					content: attachIals(helpers.parseChildren(token.tokens ?? [])),
				},
});

// The long form stays for a box Ruby could not read back: inside a quote or a list, or with a body that has unpaired `:::` lines.
export const withContainer = (ext: Node) =>
	ext.extend({
		renderMarkdown(node: JSONContent, helpers: MarkdownRendererHelpers, ctx: RenderContext) {
			const m = boxIal(node.attrs?.ial);
			const names = m && !node.attrs?.long ? shortNames(m[1]) : null;
			if (!m || names === null) return this.parent?.(node, helpers, ctx);
			const body = helpers.renderChildren(node.content ?? [], "\n\n").replace(/\n+$/, "");
			if (!balanced(body)) return this.parent?.(node, helpers, ctx);
			return `:::${names}${m[2] ? ` {${m[2]}}` : ""}\n${body}\n:::`;
		},
	});
